import {makeStyles} from 'tss-react/mui';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, Divider, Grid, HtmlTrans, KeyValue, SectionHeader, Typography} from 'web-library';
import {useHistory, useLocation} from 'react-router-dom';
import RouteKey from '../../routes/key';
import {
  useGetExamLevelWithdrawal,
  usePostCreateExamWithdrawal,
} from '../../api/candidate/exam/@candidate/queries-services/queries';
import {parseLocationSearch} from '../../utils/StringUtils';
import { useWOGAA } from '../../hooks/wogaa';

const useStyles = makeStyles()((theme) => ({
  title: {
    marginBottom: theme.spacing(2),
  },
  submitForm: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  link: {
    color: theme.color.link.linkActive01,
  },
  iconButton: {
    padding: theme.spacing(1),
    margin: 0,
    marginLeft: theme.spacing(2),
  },
  formContainer: {
    marginTop: theme.spacing(4),
    flexDirection: 'column',
    '& .MuiFormLabel-root': {
      marginBottom: theme.spacing(2),
    },
  },
  spacing: {
    marginBottom: theme.spacing(6),
  },
  actionButton: {
    marginRight: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(7),
    marginRight: theme.spacing(2),
  },
  paragraph: {
    marginBottom: theme.spacing(1),
  },
  a: {
    textDecoration: 'none',
    color: theme.color.link.linkDefault01,
  },
}));

const {withdrawal} = RouteKey.child;
const {reason, examRegistrations} = RouteKey.withdrawal.child;

const GridKeyValue = ({label, value}: { label: string; value: string | null }) => (
    <Grid xl={5} lg={6} md={12} sm={12} xs={12}>
      <KeyValue keyText={label} valueText={value || '-'} capitalize={false}/>
    </Grid>
);

const WithdrawFromExam = () => {
  const {classes} = useStyles();
  const {t} = useTranslation();
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState<string>();
  const location = useLocation();
  const {token = ''} = parseLocationSearch(location.search);

  const {data} = useGetExamLevelWithdrawal(
      {
        token: token
      },
      ({response}) => setErrorMessage(response?.data.message)
  );

  const createWithdrawal = usePostCreateExamWithdrawal(
      (res) => {
        history.push(`/${withdrawal}/${examRegistrations.route}/${res.data.uuid}/${reason.route}`);
      },
      (res) => {
        if (res.response.data.code === '1604') {
          setErrorMessage(t('withdrawal->>WithdrawFromExam.RegistrationUnderProcessing'));
        } else if (res?.response?.data?.code === '1014' || res?.response?.data?.code === '1013' || res?.response?.data?.code === '1019') {
          setErrorMessage(res?.response?.data?.message!);
        }
      }
  );

  const { transactionStart } = useWOGAA('myexamsseab-6453');

  /** WOGAA Registration withdrawal transaction tracking start **/
  transactionStart();


  return (
      <>
        <SectionHeader title="Important"/>
        <ul>
          <li className={classes.paragraph}>
            <Typography variant="body1">{t('withdrawal->>WithdrawFromExam.Content1')}</Typography>
            <ul>
              <li className={classes.paragraph}>
                <Typography variant="body1">
                  {t('withdrawal->>WithdrawFromExam.Content1Sub1')}
                </Typography>
              </li>
            </ul>
            <li className={classes.paragraph}>
              <Typography variant="body1">{t('withdrawal->>WithdrawFromExam.Content2')}</Typography>
            </li>
            <li className={classes.paragraph}>
              <Typography variant="body1">{t('withdrawal->>WithdrawFromExam.Content3')}</Typography>
            </li>
            <li className={classes.paragraph}>
              <Typography variant="body1">{t('withdrawal->>WithdrawFromExam.Content4')}</Typography>
            </li>
            <li className={classes.paragraph}>
              <Typography variant="body1">{t('withdrawal->>WithdrawFromExam.Content5')}</Typography>
            </li>
          </li>
        </ul>
        <Typography variant="body1" className={classes.spacing}>
          <HtmlTrans
              i18nKey="withdrawal->>WithdrawFromExam.ForMoreInformation"
              values={{
                aClass: classes.a,
              }}
          />
        </Typography>

        <Divider/>
        <SectionHeader title="Get Started"/>
        <Typography variant="body1">
          {t(
              'withdrawal->>WithdrawFromExam.You are about to withdraw from the following exam registration.'
          )}
        </Typography>
        <Grid className={classes.formContainer} container>
          {data && (
              <>
                <GridKeyValue label="Application ID" value={data.applicationId}/>

                <GridKeyValue label="Exam Level(s)" value={data.examLevel.toString()}/>
              </>
          )}
        </Grid>
        {errorMessage && (
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
        )}
        <Button
            id="button-resitReceipt-goToRegisteredExams"
            className={classes.button}
            onClick={() => {
              if (data) {
                createWithdrawal.mutate({
                  data: {uuid: data.uuid},
                  params: {token: token}
                });
              }
            }}
            disabled={!data?.isWithdrawOpen}
        >
          {t('withdrawal->>WithdrawFromExam.Withdraw from Exam')}
        </Button>
      </>
  );
};

export default WithdrawFromExam;
